'use client';

import { BigNum } from '@drift-labs/sdk';
import { useMemo } from 'react';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import React from 'react';
import AccountDepositsCell from 'src/components/AccountsTable/AccountDepositsCell';
import { cm } from 'src/utils/classNames';
import HealthDisplay from 'src/utils/HealthDisplay';
import { createColumnHelper } from '@tanstack/react-table';
import HistoryTable from 'src/components/HistoryTables/HistoryTable';
import useAccountsTable, {
	AccountsTableItem,
} from 'src/components/PortfolioPage/PortfolioAccountsSubPage/AccountsTable/hooks/useAccountsTable';
import { AccountsTableProps } from 'src/components/PortfolioPage/PortfolioAccountsSubPage/AccountsTable/types';
import ReclaimableRentContent from 'src/components/ReclaimableRentContent';
import SkeletonValuePlaceholder from 'src/components/SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import AccountsTableMobileCard from 'src/components/PortfolioPage/PortfolioAccountsSubPage/AccountsTable/AccountsTableMobileCard';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import AccountsTableNameCell from 'src/components/PortfolioPage/PortfolioAccountsSubPage/AccountsTable/cells/AccountsTableNameCell';
import AccountsTableActionCell from 'src/components/PortfolioPage/PortfolioAccountsSubPage/AccountsTable/cells/AccountsTableActionCell';
import AccountsTableDelegateCell from 'src/components/PortfolioPage/PortfolioAccountsSubPage/AccountsTable/cells/AccountsTableDelegateCell';
import AccountsTableFooter from 'src/components/PortfolioPage/PortfolioAccountsSubPage/AccountsTable/AccountsTableFooter';
import { ACCOUNTS_TABLE_BODY_CELL_CLASSNAME } from 'src/components/PortfolioPage/PortfolioAccountsSubPage/AccountsTable/consts';

const columnHelper = createColumnHelper<AccountsTableItem>();

const AccountsTable = ({ accounts, variant = 'main' }: AccountsTableProps) => {
	const {
		data,
		userStatsAccount,
		claimingRentAccountId,
		hasNonEmbeddedWalletDelegate,
		isDelegateVariant,
		numAccountsAtRisk,
		sumDeposits,
		sumBorrows,
		sumNetUsdValue,
		openEditAccountModal,
		openDeleteAccountModal,
		openDepositModal,
		openWithdrawModal,
		openTransferModal,
		handleReclaimRent,
		openUpdateDelegateModal,
	} = useAccountsTable({ accounts, variant });

	const columns = useMemo(() => {
		const baseColumns = [
			columnHelper.accessor('accountName', {
				header: 'Account Name',
				cell: ({ row: { original: account } }) => (
					<AccountsTableNameCell
						accountName={account.accountName}
						authority={account.authority}
						pubKey={account.pubKey}
						userId={account.userId}
						isSuperStakeAccount={account.isSuperStakeAccount}
						isLpAccount={account.isLpAccount}
						isIsolatedAccount={account.isIsolatedAccount}
						isDelegateVariant={isDelegateVariant}
						openEditAccountModal={openEditAccountModal}
					/>
				),
				meta: {
					width: { ratio: isDelegateVariant ? 20 : 12, minPx: 160 },
					bodyCellClassName: cm(
						'flex w-full items-center gap-x-xs',
						isDelegateVariant && 'flex-col items-start gap-y-xs',
						ACCOUNTS_TABLE_BODY_CELL_CLASSNAME
					),
				},
			}),
			columnHelper.accessor('acctSpotDeposits', {
				header: 'Deposits',
				cell: ({ getValue }) => (
					<AccountDepositsCell spotDeposits={getValue()} />
				),
				meta: {
					width: { ratio: isDelegateVariant ? 16 : 12, minPx: 120 },
					bodyCellClassName: ACCOUNTS_TABLE_BODY_CELL_CLASSNAME,
				},
			}),
			columnHelper.accessor('acctBorrows', {
				header: 'Borrows',
				cell: ({ getValue }) => (
					<NumberDisplay
						value={getValue()}
						type="currency"
						millify={{ threshold: 10_000, precision: 4 }}
						isAccountValueToHide
					/>
				),
				meta: {
					width: { ratio: isDelegateVariant ? 16 : 12, minPx: 120 },
					bodyCellClassName: ACCOUNTS_TABLE_BODY_CELL_CLASSNAME,
				},
			}),
			columnHelper.accessor('acctNetValue', {
				header: 'Net Value',
				cell: ({ getValue }) => (
					<NumberDisplay
						value={getValue()}
						type="currency"
						millify={{ threshold: 10_000, precision: 4 }}
						isAccountValueToHide
					/>
				),
				meta: {
					width: { ratio: isDelegateVariant ? 16 : 12, minPx: 120 },
					bodyCellClassName: ACCOUNTS_TABLE_BODY_CELL_CLASSNAME,
				},
			}),
			columnHelper.accessor('health', {
				header: 'Health',
				cell: ({ getValue }) => (
					<HealthDisplay health={getValue()} valueClassName="typo-t3" />
				),
				meta: {
					width: { ratio: isDelegateVariant ? 16 : 11, minPx: 120 },
					bodyCellClassName: ACCOUNTS_TABLE_BODY_CELL_CLASSNAME,
				},
			}),
		];

		if (isDelegateVariant) {
			return [
				...baseColumns,
				columnHelper.accessor('openPositionCount', {
					header: 'Open Positions',
					cell: ({ getValue }) => (
						<NumberDisplay value={BigNum.from(getValue())} type="number" />
					),
					meta: {
						width: { ratio: 16, minPx: 120 },
						bodyCellClassName: ACCOUNTS_TABLE_BODY_CELL_CLASSNAME,
					},
				}),
			];
		}

		return [
			...baseColumns,
			...(hasNonEmbeddedWalletDelegate
				? [
						columnHelper.accessor('delegate', {
							header: 'Delegate',
							cell: ({ row: { original: account } }) => (
								<AccountsTableDelegateCell
									userId={account.userId}
									authority={account.authority}
									delegate={account.delegate}
									isDelegated={account.isDelegated}
									isDelegatedToEmbeddedWallet={
										account.isDelegatedToEmbeddedWallet
									}
									isDelegatedToNonEmbeddedWallet={
										account.isDelegatedToNonEmbeddedWallet
									}
									openUpdateDelegateModal={openUpdateDelegateModal}
								/>
							),
							meta: {
								width: { ratio: 12, minPx: 120 },
								bodyCellClassName: ACCOUNTS_TABLE_BODY_CELL_CLASSNAME,
							},
						}),
					]
				: []),
			columnHelper.accessor('rent', {
				header: () => (
					<DefinitionTooltip definitionId="accountsReclaimableRent">
						Reclaimable Rent
					</DefinitionTooltip>
				),
				cell: ({ getValue, row }) =>
					getValue().loaded && userStatsAccount ? (
						<ReclaimableRentContent
							userStatsAccount={userStatsAccount}
							accountDisplayData={row.original}
						/>
					) : (
						<SkeletonValuePlaceholder className="h-4 w-20" loading />
					),
				meta: {
					width: { ratio: 11, minPx: 120 },
					bodyCellClassName: ACCOUNTS_TABLE_BODY_CELL_CLASSNAME,
				},
			}),
			columnHelper.display({
				header: 'Action',
				cell: ({ row: { original: account } }) => (
					<AccountsTableActionCell
						userId={account.userId}
						authority={account.authority}
						delegate={account.delegate}
						isIsolatedAccount={account.isIsolatedAccount}
						isDelegatedToNonEmbeddedWallet={
							account.isDelegatedToNonEmbeddedWallet
						}
						hasClaimableRent={account.hasClaimableRent}
						claimingRentAccountId={claimingRentAccountId}
						handleReclaimRent={handleReclaimRent}
						openDepositModal={openDepositModal}
						openWithdrawModal={openWithdrawModal}
						openTransferModal={openTransferModal}
						openUpdateDelegateModal={openUpdateDelegateModal}
						openDeleteAccountModal={openDeleteAccountModal}
					/>
				),
				meta: {
					width: { ratio: 18, minPx: 260 },
					headerCellClassName: 'text-right',
					bodyCellClassName: 'flex items-center gap-x-xs justify-end',
				},
			}),
		];
	}, [
		isDelegateVariant,
		hasNonEmbeddedWalletDelegate,
		userStatsAccount,
		claimingRentAccountId,
		handleReclaimRent,
		openDepositModal,
		openWithdrawModal,
		openTransferModal,
		openUpdateDelegateModal,
		openDeleteAccountModal,
		openEditAccountModal,
	]);

	return (
		<HistoryTable
			dataTableProps={{
				data,
				columns,
				emptyMessage: 'No accounts found',
				className: 'rounded-sm w-full',
				dataTestId: 'portfolio-accounts-table',
				footer: (
					<AccountsTableFooter
						numAccountsAtRisk={numAccountsAtRisk}
						sumBorrows={sumBorrows}
						sumDeposits={sumDeposits}
						sumNetUsdValue={sumNetUsdValue}
						numColumns={columns.length}
					/>
				),
			}}
			mobileCard={({ record: account }) => (
				<AccountsTableMobileCard
					key={account.pubKey.toString()}
					account={account}
					claimingRentAccountId={claimingRentAccountId}
					openDepositModal={openDepositModal}
					openWithdrawModal={openWithdrawModal}
					openTransferModal={openTransferModal}
					openUpdateDelegateModal={openUpdateDelegateModal}
					openEditAccountModal={openEditAccountModal}
					openDeleteAccountModal={openDeleteAccountModal}
					handleReclaimRent={handleReclaimRent}
					isDelegateVariant={isDelegateVariant}
					userStatsAccount={userStatsAccount}
				/>
			)}
			contentGuardClassName="rounded-t-sm"
		/>
	);
};

export default React.memo(AccountsTable);
